import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { addYears, subDays } from 'date-fns';

import type { DashboardCustomer } from '@models/customers';
import type { BrandCode } from '@models/payments';
import {
  useCustomerByIdQuery,
  useCustomersInfiniteQuery,
  type GetCustomersInfiniteParams,
} from '@features/customers/queries';
import { useDebounce } from '@hooks/useDebounce';
import { useToast } from '@hooks/useToast';
import { formatYmd } from '@utils/date';

const PAGE_SIZE = 14;

const CUSTOMERS_FROM_DATE = '2023-08-30';

export function useCustomersList() {
  const [search, setSearchValue] = useState('');
  const [hasDebt, setHasDebt] = useState(false);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [testUser, setTestUser] = useState(false);
  const [brandCode, setBrandCode] = useState<BrandCode | 'all'>('all');

  const [fromDate, setFromDate] = useState(() => formatYmd(subDays(new Date(), 90)));
  const [toDate, setToDate] = useState(() => formatYmd(new Date()));
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();

  const debouncedSearch = useDebounce(search, 300);
  const trimmedSearch = debouncedSearch.trim();

  const queryParams: GetCustomersInfiniteParams = {
    limit: PAGE_SIZE,
    fromDate: fromDate || CUSTOMERS_FROM_DATE,
    toDate: toDate || formatYmd(addYears(new Date(), 1)),
    searchQuery: trimmedSearch.length > 0 ? trimmedSearch : undefined,
    brandCode: brandCode === 'all' ? undefined : brandCode,
    hasDebt: hasDebt || undefined,
    hasActiveSubscription: hasActiveSubscription || undefined,
    testUser: testUser || undefined,
  };

  const { data, isLoading, isFetching, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useCustomersInfiniteQuery(queryParams);
  const toast = useToast();
  const errorShownRef = useRef(false);
  useEffect(() => {
    if (isError && !errorShownRef.current) {
      toast.error('Failed to load customers');
      errorShownRef.current = true;
    }
    if (!isError) errorShownRef.current = false;
  }, [isError, toast]);

  const rows: DashboardCustomer[] = data?.pages.flatMap((page) => page.data) ?? [];

  const selectedFromList = rows.find((row) => row.id === customerId);
  // A shared deep link may point at a customer outside the loaded page
  const { data: customerById } = useCustomerByIdQuery(
    customerId,
    Boolean(customerId) && !selectedFromList,
  );

  // In-list row wins; for a deep link show the directly-fetched customer (null while it loads or if
  // the id is unknown, so we never flash the wrong one); with no id in the URL, default to the first.
  const selected = selectedFromList ?? (customerId ? customerById : rows[0]) ?? null;

  const selectCustomer = (id: string) => {
    navigate(`/dashboard/customers/${id}`);
  };

  const setSearch = (v: string) => {
    setSearchValue(v);
  };

  const toggleHasDebt = () => setHasDebt((v) => !v);
  const toggleHasActiveSubscription = () => setHasActiveSubscription((v) => !v);
  const toggleTestUser = () => setTestUser((v) => !v);

  const setDateRange = (from: string, to: string) => {
    setFromDate(from);
    setToDate(to);
  };

  return {
    search,
    setSearch,
    hasDebt,
    toggleHasDebt,
    hasActiveSubscription,
    toggleHasActiveSubscription,
    testUser,
    toggleTestUser,
    brandCode,
    setBrandCode,
    fromDate,
    toDate,
    setDateRange,
    rows,
    selected,
    selectCustomer,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    isLoading,
    isFetching,
  };
}
